import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button, Input, Icon, Checkbox } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import {
  passkeySupported, loginWithPasskey, passkeyAutofillSupported, cancelPendingPasskey,
  listPasskeys, passkeyExistsFor,
} from '../features/passkey/passkeyApi';
import { PasskeyEnrollPrompt } from '../features/passkey/PasskeyEnrollPrompt';
import './Login.css';

// User-facing copy for each error the SSO callback can bounce back
// with. Anything not in this map falls back to a generic message so
// the page never blows up on an unknown code.
const SSO_ERROR_MESSAGES: Record<string, string> = {
  no_account: 'אין לך גישה למערכת. פנה למנהל המערכת כדי לקבל חשבון.',
  inactive: 'החשבון שלך מושבת. פנה למנהל המערכת.',
  twofa_setup_required: 'נדרשת השלמת 2FA לפני התחברות. התחבר תחילה עם סיסמה כדי להגדיר.',
  cancelled: 'ההתחברות בוטלה.',
  bad_state: 'הקישור לחיבור פג תוקף. נסה שוב.',
  missing_token: 'התקבל קישור התחברות לא תקין. נסה שוב.',
  sso_finalize_failed: 'אירעה שגיאה בסיום ההתחברות. נסה שוב.',
  sso_not_configured: 'התחברות באמצעות Microsoft אינה מוגדרת בשרת.',
};

const REMEMBER_EMAIL_KEY = 'mylo.remember_email';
// "Not now" on the passkey offer backs off for a week instead of asking on
// every single login, which is what makes these prompts feel like nagging.
const ENROLL_SNOOZE_KEY = 'mylo.passkey_enroll_snooze';
const ENROLL_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const FEATURES = [
  {
    icon: 'rocket_launch',
    title: 'ניהול הצעות מחיר',
    desc: 'יצירה, חתימה דיגיטלית ושליחה אוטומטית ללקוחות',
  },
  {
    icon: 'groups',
    title: 'CRM מלא',
    desc: 'לקוחות, חברות, עסקאות ופעילויות במקום אחד',
  },
  {
    icon: 'analytics',
    title: 'דוחות ויעדים',
    desc: 'מעקב בזמן אמת אחרי יעדי מכירות וביצועים',
  },
];

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, setAuthData } = useAuth();
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const canUsePasskey = passkeySupported();
  // Shown after a successful PASSWORD login when the account has no passkey yet
  // — the highest-converting enrollment moment. Never a gate; see the prompt.
  const [offerEnroll, setOfferEnroll] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Surface SSO callback errors that the server bounced us back with.
  // Read once on mount, clear the param from the URL so a refresh
  // doesn't keep re-displaying the banner.
  useEffect(() => {
    const ssoError = searchParams.get('error');
    if (!ssoError) return;
    const msg = SSO_ERROR_MESSAGES[ssoError] || 'אירעה שגיאה בהתחברות. נסה שוב.';
    setError(msg);
    searchParams.delete('error');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kick off the Microsoft SSO flow. We navigate the whole page to
  // the server's /authorize endpoint instead of fetching it — that
  // way the Set-Cookie header carrying the CSRF binding-id lands on
  // the same browser that Microsoft will eventually redirect back
  // (Set-Cookie from a cross-origin fetch would be silently dropped
  // without withCredentials + CORS-allow-credentials).
  const startSSO = () => {
    if (ssoLoading) return;
    setError('');
    setSsoLoading(true);
    window.location.href = `${API_URL}/api/auth/sso/microsoft/authorize`;
  };

  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_EMAIL_KEY) || '');
  const [password, setPassword] = useState('');
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  // 2FA enrollment from the login page (require2FA on, but TOTP not set up).
  const [setup2FA, setSetup2FA] = useState<{ token: string; qrCode: string; secret: string } | null>(null);
  const [setupCode, setSetupCode] = useState('');
  const [settingUp, setSettingUp] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(REMEMBER_EMAIL_KEY));
  const [capsLock, setCapsLock] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const id = setInterval(() => setRetryAfter((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [retryAfter]);

  const onPasswordKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeof e.getModifierState === 'function') {
      setCapsLock(e.getModifierState('CapsLock'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) return setError('נא להזין אימייל');
    if (!password.trim()) return setError('נא להזין סיסמה');
    if (requires2FA && !twoFactorToken.trim()) return setError('נא להזין קוד אימות');

    setLoading(true);
    try {
      const result = await login(email, password, requires2FA ? twoFactorToken : undefined);

      if (result.requires2FA) {
        setRequires2FA(true);
        setLoading(false);
        return;
      }
      if (result.requires2FASetup) {
        // Enroll in 2FA right here using the setup-scoped token: fetch the QR,
        // then the user scans + verifies, which enables 2FA and lets them in.
        try {
          const res = await axios.post(`${API_URL}/api/2fa/setup`, {}, {
            headers: { Authorization: `Bearer ${result.setupToken}` },
          });
          const otpauthUrl: string = res.data.otpauthUrl || '';
          const secretMatch = /[?&]secret=([A-Z2-7]+)/i.exec(otpauthUrl);
          setSetup2FA({
            token: result.setupToken,
            qrCode: res.data.qrCode,
            secret: secretMatch ? secretMatch[1] : '',
          });
        } catch {
          setError('שגיאה בהגדרת אימות דו-שלבי. פנה למנהל המערכת.');
        }
        setLoading(false);
        return;
      }

      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }

      if (await shouldOfferEnrollment()) {
        setOfferEnroll(true);
        setLoading(false);
        return; // the prompt navigates on, whichever way the user answers
      }

      navigate('/dashboard');
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const retry = Number(err?.response?.headers?.['retry-after'] ?? err?.retryAfter ?? 0);
      if (status === 429 || retry > 0) {
        setRetryAfter(retry > 0 ? retry : 60);
        setError('יותר מדי ניסיונות. נסה שוב בעוד מספר שניות.');
      } else {
        const msg = err.message || 'שגיאה באימות';
        if (msg.includes('Invalid credentials') || msg.includes('Authentication failed')) {
          setError('אימייל או סיסמה לא נכונים');
        } else {
          setError(msg);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Whether to offer passkey enrollment after this password login.
   *
   * Offered only when it can actually succeed and won't annoy: the browser
   * supports passkeys, the account has none yet, and the user hasn't recently
   * said "not now". A failure to answer any of these questions falls through to
   * "don't offer" — the dashboard must never be blocked by this check.
   */
  const shouldOfferEnrollment = async (): Promise<boolean> => {
    if (!canUsePasskey) return false;
    const snoozedUntil = Number(localStorage.getItem(enrollSnoozeKey()) || 0);
    if (Date.now() < snoozedUntil) return false;
    try {
      // Bounded: this sits on the critical path of a login that has ALREADY
      // succeeded, so a slow or hung request must never strand an authenticated
      // user on this page. Losing the race just skips the offer.
      const rows = await Promise.race([
        listPasskeys(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 2500)),
      ]);
      return rows !== null && rows.length === 0;
    } catch {
      return false;
    }
  };

  const finishEnrollment = () => {
    localStorage.removeItem(enrollSnoozeKey());
    navigate('/dashboard');
  };

  const skipEnrollment = () => {
    // Back off for a while rather than asking again next login — nagging a user
    // who declined is what drives them away from the flow entirely.
    localStorage.setItem(enrollSnoozeKey(), String(Date.now() + ENROLL_SNOOZE_MS));
    navigate('/dashboard');
  };

  /** Snooze is per-account: on a shared browser one person's "not now" must not
   *  silence the offer for everyone else. */
  const enrollSnoozeKey = () => `${ENROLL_SNOOZE_KEY}:${email.trim().toLowerCase()}`;

  const goBack = () => {
    setRequires2FA(false);
    setTwoFactorToken('');
    setError('');
  };

  /**
   * Conditional mediation ("autofill UI"): arm a passkey request on mount so a
   * saved passkey can be offered inline from the email field. This is passive —
   * it only lights up the browser's own autofill dropdown — so it is the
   * fallback layer, not the thing that makes the prompt appear on its own.
   */
  const autofillCancelledRef = React.useRef(false);
  const armAutofill = React.useCallback(async () => {
    if (!canUsePasskey) return;
    try {
      if (!(await passkeyAutofillSupported())) return;
      const { token, refreshToken, user } = await loginWithPasskey(undefined, true);
      if (autofillCancelledRef.current) return;
      setAuthData(token, user, refreshToken);
      navigate('/dashboard');
    } catch {
      // Cancelled, unsupported, or no passkey chosen — the user just types a
      // password instead. Never surface an error for an offer they ignored.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUsePasskey]);

  useEffect(() => {
    autofillCancelledRef.current = false;
    armAutofill();
    return () => {
      autofillCancelledRef.current = true;
      // Also abort at the BROWSER level: a local flag alone would leave the
      // ceremony holding the one WebAuthn slot across a remount, blocking the
      // next request.
      cancelPendingPasskey();
    };
  }, [armAutofill]);

  /**
   * Auto-prompt once a full email has been entered — the behaviour users expect
   * from Apple/Google: you finish typing the address and the Touch ID sheet
   * comes up by itself, with the button left as a manual fallback.
   *
   * Passive autofill alone does NOT open a sheet, so this starts a real (modal)
   * ceremony — but ONLY after confirming the address actually has a passkey.
   * Prompting unconditionally would throw a biometric sheet at every user who
   * has never enrolled, which is worse than no feature at all.
   */
  const autoPromptedRef = React.useRef<string>('');
  /** Returns whether a passkey sheet was actually shown. */
  const tryAutoPasskey = async (): Promise<boolean> => {
    const addr = email.trim().toLowerCase();
    if (!canUsePasskey || passkeyLoading) return false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) return false;
    if (autoPromptedRef.current === addr) return false;

    // Cheap, sheet-free probe first — no passkey for this address means no
    // prompt at all. Marked as attempted only once we know we can prompt, so a
    // transient network failure doesn't permanently burn the one shot.
    if (!(await passkeyExistsFor(addr))) return false;
    autoPromptedRef.current = addr;

    cancelPendingPasskey(); // free the single WebAuthn slot held by autofill
    setPasskeyLoading(true);
    try {
      const { token, refreshToken, user } = await loginWithPasskey(addr);
      setAuthData(token, user, refreshToken);
      navigate('/dashboard');
    } catch (err: any) {
      // A dismissed sheet is a normal "no" — we prompted on their behalf, so it
      // must stay silent. A real failure (e.g. a credential the server no longer
      // has) does get surfaced, and the address is released so it can be retried.
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        autoPromptedRef.current = '';
        setError('כניסה עם מפתח גישה נכשלה. אפשר להיכנס עם סיסמה.');
      }
    } finally {
      setPasskeyLoading(false);
      // Re-arm passive autofill, which the ceremony above cancelled.
      armAutofill();
    }
    return true;
  };

  // Passkey login: prompt the authenticator, then set the session directly.
  const handlePasskeyLogin = async () => {
    const hadEmail = !!email.trim();
    setPasskeyLoading(true);
    setError('');
    // Only one WebAuthn ceremony can be in flight, and the autofill request
    // armed on mount is holding that slot — clear it or this call is rejected.
    cancelPendingPasskey();
    try {
      const { token, refreshToken, user } = await loginWithPasskey(email.trim() || undefined);
      setAuthData(token, user, refreshToken);
      navigate('/dashboard');
    } catch (err: any) {
      // NotAllowedError = user cancelled OR no matching passkey on the device
      // (we can't tell which — the server deliberately doesn't reveal whether a
      // passkey exists, to avoid an account-enumeration oracle). Stay silent so
      // the user simply falls back to password.
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') return;

      // Without an email the server can't send allowCredentials, so the browser
      // offers EVERY passkey it holds for this domain — including stale ones
      // whose server-side record is gone, which then fail as "not recognized".
      // Point the user at the fix instead of a dead end. This branches only on
      // whether THEY typed an email, never on whether an account exists, so it
      // stays free of the enumeration oracle the server guards against.
      if (err?.response?.status === 401 && !hadEmail) {
        setError('לא זוהה מפתח גישה תואם. הקלד את האימייל שלך ונסה שוב.');
      } else {
        setError('כניסה עם מפתח גישה נכשלה. נסה סיסמה.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // Complete 2FA enrollment: verify the code against the setup token, which
  // enables 2FA. Then re-login normally (now with the TOTP code) to get in.
  const handleVerifySetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setup2FA || setupCode.trim().length < 6) return setError('נא להזין קוד בן 6 ספרות');
    setSettingUp(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/2fa/verify`,
        { token: setupCode.trim() },
        { headers: { Authorization: `Bearer ${setup2FA.token}` } },
      );
      // 2FA is now enabled — log in for real, supplying the same code.
      const result = await login(email, password, setupCode.trim());
      if (result.requires2FA) {
        // Code already consumed by verify; ask for a fresh one on the 2FA screen.
        setSetup2FA(null);
        setSetupCode('');
        setRequires2FA(true);
        setError('האימות הופעל. הזן קוד חדש מהאפליקציה כדי להיכנס.');
      } else {
        navigate('/dashboard');
      }
    } catch {
      setError('קוד שגוי. נסה שוב עם הקוד הנוכחי מהאפליקציה.');
    } finally {
      setSettingUp(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Brand panel (desktop only) */}
      <aside className="auth-brand-panel">
        <div className="auth-brand-grid" />
        <div className="auth-brand-orb auth-brand-orb-1" />
        <div className="auth-brand-orb auth-brand-orb-2" />
        <div className="auth-brand-orb auth-brand-orb-3" />

        <div className="auth-brand-content">
          <div className="auth-brand-logo">
            <img src="/matat-logo.svg" alt="Matat" />
          </div>

          <h2 className="auth-brand-title">
            ניהול עסקי<br />
            פשוט וחכם.
          </h2>
          <p className="auth-brand-subtitle">
            כל מה שצריך כדי לנהל לקוחות, הצעות מחיר ועסקאות —
            במערכת אחת, בעברית, עם חוויה מודרנית.
          </p>

          <div className="auth-brand-features">
            {FEATURES.map((f) => (
              <div key={f.title} className="auth-brand-feature">
                <div className="auth-brand-feature-icon">
                  <Icon name={f.icon} size={22} />
                </div>
                <div>
                  <div className="auth-brand-feature-title">{f.title}</div>
                  <div className="auth-brand-feature-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="auth-brand-footer">
          © {new Date().getFullYear()} Matat CRM · כל הזכויות שמורות
        </div>
      </aside>

      {/* Form panel */}
      <main className="auth-form-panel">
        <div className="auth-form-card">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-6">
            <img src="/matat-logo.svg" alt="Matat" className="h-12 w-auto mx-auto" />
          </div>

          {/* The enrollment prompt carries its own heading and icon. */}
          <div className={`mb-7 ${offerEnroll ? 'hidden' : ''}`}>
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-bold mb-4">
              <Icon name={requires2FA ? 'security' : 'waving_hand'} size={14} />
              {requires2FA ? 'אימות דו-שלבי' : 'ברוכים הבאים'}
            </div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900 leading-tight">
              {requires2FA ? 'אמת את הזהות שלך' : 'התחברות למערכת'}
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              {requires2FA
                ? `הזן את הקוד בן 6 הספרות מאפליקציית האימות שלך`
                : 'הזן את פרטי ההתחברות שלך כדי להיכנס ל-Matat CRM'}
            </p>
          </div>

          {offerEnroll ? (
            <PasskeyEnrollPrompt onDone={finishEnrollment} onSkip={skipEnrollment} />
          ) : setup2FA ? (
            <form onSubmit={handleVerifySetup} className="space-y-3.5">
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                מנהל המערכת דורש אימות דו-שלבי. סרוק את הקוד באפליקציית Authenticator
                (Google Authenticator / Microsoft Authenticator) והזן את הקוד כדי להשלים.
              </div>
              <div className="flex justify-center">
                {setup2FA.qrCode && (
                  <img src={setup2FA.qrCode} alt="קוד QR לאימות דו-שלבי" className="w-44 h-44 rounded-lg border border-slate-200" />
                )}
              </div>
              {setup2FA.secret && (
                <p className="text-center text-[11px] text-slate-500">
                  קוד ידני: <span className="font-mono tracking-wider">{setup2FA.secret}</span>
                </p>
              )}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                  קוד מהאפליקציה
                </label>
                <Input
                  icon="pin"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={setupCode}
                  onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoFocus
                />
              </div>
              {error && <div className="text-xs text-red-600">{error}</div>}
              <Button type="submit" disabled={settingUp || setupCode.length < 6} className="w-full">
                {settingUp ? 'מפעיל…' : 'הפעל והיכנס'}
              </Button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {!requires2FA && (
              <>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                    אימייל
                  </label>
                  <Input
                    icon="mail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    // Prompt for a passkey once the address is complete —
                    // leaving the field, or pressing Enter/Tab in it.
                    onBlur={tryAutoPasskey}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      if (password) return; // normal submit; don't interfere
                      // With no password yet, Enter means "continue with this
                      // address". Suppress the submit ONLY if a passkey prompt
                      // will actually follow — otherwise let the form submit so
                      // the usual "enter a password" validation still shows.
                      e.preventDefault();
                      tryAutoPasskey().then((prompted) => {
                        if (!prompted) setError('נא להזין סיסמה');
                      });
                    }}
                    placeholder="you@company.com"
                    // "webauthn" is what lets the browser offer a saved passkey
                    // inline in this field (conditional mediation) — without it
                    // the autofill prompt never appears.
                    autoComplete="username webauthn"
                    inputSize="lg"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                      סיסמה
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-xs font-semibold text-primary hover:underline"
                    >
                      שכחתי סיסמה
                    </Link>
                  </div>
                  <Input
                    icon="lock"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={onPasswordKey}
                    onKeyUp={onPasswordKey}
                    onBlur={() => setCapsLock(false)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    inputSize="lg"
                    required
                    endAdornment={
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="size-8 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center transition-colors"
                        aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                        tabIndex={-1}
                      >
                        <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={18} />
                      </button>
                    }
                  />
                  {capsLock && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                      <Icon name="keyboard_capslock" size={14} />
                      Caps Lock פועל
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    size="sm"
                  />
                  <span className="text-sm text-slate-600">זכור אותי במכשיר הזה</span>
                </label>
              </>
            )}

            {requires2FA && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider text-center">
                  קוד אימות
                </label>
                <Input
                  value={twoFactorToken}
                  onChange={(e) => setTwoFactorToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  inputSize="lg"
                  className="text-center tabular-nums text-2xl font-bold tracking-[0.75em]"
                  autoFocus
                />
              </div>
            )}

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 flex items-start gap-2 animate-in fade-in slide-in-from-top-1">
                <Icon name="error" size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm text-red-900">{error}</div>
                <button
                  type="button"
                  onClick={() => setError('')}
                  className="size-6 rounded hover:bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0"
                  aria-label="סגור"
                >
                  <Icon name="close" size={14} />
                </button>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              fullWidth
              disabled={retryAfter > 0}
              icon={retryAfter > 0 ? 'hourglass_empty' : requires2FA ? 'verified' : 'arrow_back'}
            >
              {retryAfter > 0
                ? `נסה שוב עוד ${retryAfter} שניות`
                : requires2FA ? 'אמת והתחבר' : 'התחבר למערכת'}
            </Button>

            {requires2FA && (
              <button
                type="button"
                onClick={goBack}
                className="w-full text-center text-sm font-semibold text-slate-500 hover:text-slate-900 py-2 transition-colors"
              >
                חזרה להזנת פרטים
              </button>
            )}
          </form>
          )}

          {/* Alternative login: Microsoft 365 SSO. Only existing
              users can sign in this way — see the rejection rules in
              server/routes/auth-sso-microsoft.ts. Hidden during the
              2FA step because the user is already mid-flow on the
              password path. */}
          {!requires2FA && !setup2FA && (
            <>
              <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-widest text-slate-400">
                <div className="flex-1 h-px bg-slate-100" />
                <span>או</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <button
                type="button"
                onClick={startSSO}
                disabled={ssoLoading}
                className="w-full h-11 inline-flex items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-900 font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {ssoLoading ? (
                  <Icon name="progress_activity" size={18} className="animate-spin" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                    <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                    <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                    <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                  </svg>
                )}
                <span>התחבר עם Microsoft 365</span>
              </button>
              {canUsePasskey && (
                <button
                  type="button"
                  onClick={handlePasskeyLogin}
                  disabled={passkeyLoading}
                  className="mt-2 w-full h-11 inline-flex items-center justify-center gap-2 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-900 font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Icon name={passkeyLoading ? 'progress_activity' : 'key'} size={18} className={passkeyLoading ? 'animate-spin' : ''} />
                  <span>התחבר עם מפתח גישה</span>
                </button>
              )}
            </>
          )}

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-400">
            <Icon name="shield" size={14} />
            <span>כל הנתונים מוצפנים ומאובטחים</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
